import {useCallback, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {useTranslation} from 'react-i18next';

import NodeTypeElement from './NodeTypeElement';
import {NodeConfigDialogProps} from './nodeConfigDialog.types';
import NodeIdentityPanel from './v2/NodeIdentityPanel';
import {
    renderNodeEditorV2,
    renderNodeHeaderAsideV2,
    renderNodeIdentityAsideV2
} from './v2/renderNodeEditorV2';
import {NodeBeforeSave, NodeEditorV2Props} from './v2/types';

/** The node types whose dialog opens `lg` and can be enlarged to cover the window. */
const WIDE_NODE_TYPES: NodeType[] = [
    NodeType.AUDIENCE, NodeType.SEND_EMAIL, NodeType.SEND_TEAM_EMAIL, NodeType.IF_CONDITION, NodeType.CONTINUE_IF
];

/** The node types whose dialog always has its full height, so the editor's main field can take the rest (the email). */
const FILL_NODE_TYPES: NodeType[] = [NodeType.SEND_TEAM_EMAIL];

/**
 * The node dialog: identity on the left, the type's editor on the right; widths per type (WIDE_NODE_TYPES,
 * manual trigger `sm`, else `md`), Audience stacks the identity above. `expanded` survives across nodes.
 */
const NodeConfigDialog = ({
    open,
    editingNode: liveNode,
    nodeConfig: liveConfig,
    setNodeConfig,
    nodeLabel: liveLabel,
    setNodeLabel,
    onClose,
    onSave,
    onDelete,
    availableForms = [],
    availableFormsLoading,
    availableSurveys = [],
    availableSurveysLoading,
    upstreamNodes = [],
    flowNodes = [],
    flowConnections = [],
    readOnly = false
}: NodeConfigDialogProps) => {
    const {t} = useTranslation();

    // Save and Delete clear the node in the closing render; keep showing it while the dialog fades out.
    const shownRef = useRef({editingNode: liveNode, nodeConfig: liveConfig, nodeLabel: liveLabel});
    if (open) shownRef.current = {editingNode: liveNode, nodeConfig: liveConfig, nodeLabel: liveLabel};
    const {editingNode, nodeConfig, nodeLabel} = shownRef.current;

    // A ref, not state: registering must not re-render the dialog.
    const beforeSaveRef = useRef<NodeBeforeSave | null>(null);
    const [saving, setSaving] = useState(false);

    const registerBeforeSave = useCallback((fn: NodeBeforeSave | null) => {
        beforeSaveRef.current = fn;
    }, []);

    // Shared by the header control and the editor (`renderNodeHeaderAsideV2`); `''` = each type's default.
    const [headerChoice, setHeaderChoice] = useState('');

    // The dialog outlives its node: reset the header choice on another node and on exit (`onExited`).
    const editingNodeId = editingNode?.id;
    useEffect(() => {
        setHeaderChoice('');
    }, [editingNodeId]);

    const stacked = editingNode?.type === NodeType.AUDIENCE;
    const isManualTrigger = editingNode?.type === NodeType.MANUAL_TRIGGER;
    const busy = saving;
    const handleClose = () => {
        if (busy) return;
        onClose();
    };
    const wide = !!editingNode && WIDE_NODE_TYPES.includes(editingNode.type);
    const maxWidth = isManualTrigger ? 'sm' : wide ? 'lg' : 'md';

    const [expanded, setExpanded] = useState(false);
    const covering = wide && expanded;
    // Enlarged, every wide dialog is a column its editor can fill; a FILL_NODE_TYPES dialog is one at its normal size too.
    const filling = covering || (!!editingNode && FILL_NODE_TYPES.includes(editingNode.type));

    const editorProps: NodeEditorV2Props | null = editingNode
        ? {
            nodeId: editingNode.id,
            upstreamNodes,
            flowNodes,
            flowConnections,
            nodeConfig,
            setNodeConfig,
            availableForms,
            availableFormsLoading,
            availableSurveys,
            availableSurveysLoading,
            registerBeforeSave,
            headerChoice,
            setHeaderChoice,
            expanded: covering,
            readOnly
        }
        : null;

    const editor = editingNode && editorProps
        ? renderNodeEditorV2(editingNode.type, editorProps)
        : null;

    const headerAside = editingNode && editorProps
        ? renderNodeHeaderAsideV2(editingNode.type, editorProps)
        : null;

    // A click on the fading dialog would save or delete a node that is no longer being edited.
    const handleDelete = () => {
        if (open) onDelete();
    };

    const handleSave = async () => {
        if (!open) return;
        const beforeSave = beforeSaveRef.current;

        if (!beforeSave) {
            onSave();
            return;
        }

        setSaving(true);
        try {
            const result = await beforeSave();
            // false: the editor has already said why; the dialog stays open, the node untouched.
            if (!result) return;

            onSave(result === true ? undefined : result.config);
        } finally {
            setSaving(false);
        }
    };

    const identityPanel = (
        <NodeIdentityPanel
            nodeType={editingNode?.type}
            nodeLabel={nodeLabel}
            setNodeLabel={setNodeLabel}
            readOnly={readOnly || busy}
            compact={stacked}
            aside={editingNode && editorProps
                ? renderNodeIdentityAsideV2(editingNode.type, editorProps)
                : null}
        />
    );

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : handleClose}
            maxWidth={covering ? false : maxWidth}
            fullWidth
            TransitionProps={{onExited: () => setHeaderChoice('')}}
            sx={{'& .MuiDialog-container': {alignItems: covering ? 'center' : 'flex-start'}}}
            PaperProps={{sx: covering
                ? {m: 3, width: 'calc(100% - 48px)', height: 'calc(100% - 48px)', maxWidth: 'none', maxHeight: 'none'}
                : {mt: 8, maxHeight: '90vh', ...(filling && {height: '90vh'})}}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
                        <Typography variant={'h6'} fontWeight={600} noWrap>
                            {t('Configure node')}
                        </Typography>

                        <NodeTypeElement nodeType={editingNode?.type}/>
                    </Stack>

                    <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{flexShrink: 0}}>
                        {headerAside}

                        {wide && (
                            <Tooltip title={covering ? t('Shrink') : t('Expand')}>
                                <IconButton onClick={() => setExpanded(value => !value)} size={'small'}
                                            aria-label={covering ? t('Shrink') : t('Expand')} aria-pressed={covering}
                                            sx={{display: {xs: 'none', md: 'inline-flex'}}}>
                                    {covering ? <CloseFullscreenIcon fontSize={'small'}/> : <OpenInFullIcon fontSize={'small'}/>}
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title={t('Close')}>
                            <span>
                                <IconButton onClick={handleClose} size={'small'} aria-label={t('Close')} disabled={busy}>
                                    <CloseIcon/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{
                p: 0,
                minHeight: 0,
                display: stacked || isManualTrigger ? 'block' : 'flex',
                flexDirection: 'column',
                overflowY: stacked ? 'auto' : {xs: 'auto', md: 'hidden'}
            }}>
                {isManualTrigger ? (
                    <Box sx={{p: 3}}>{identityPanel}</Box>
                ) : stacked ? (
                    <Stack sx={{minHeight: 0}}>
                        <Box sx={theme => ({
                            px: 3,
                            py: 2,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            bgcolor: 'background.default'
                        })}>
                            {identityPanel}
                        </Box>

                        <Box sx={{p: 3, minWidth: 0}}>
                            {editor}
                        </Box>
                    </Stack>
                ) : (
                    <Stack direction={{xs: 'column', md: 'row'}} sx={{minHeight: 0, flex: {xs: '0 0 auto', md: '1 1 auto'}}}>
                        <Box sx={theme => ({
                            width: {xs: '100%', md: 340},
                            maxWidth: {md: '38%'},
                            flexShrink: 0,
                            minHeight: 0,
                            p: 3,
                            borderRight: {xs: 'none', md: `1px solid ${theme.palette.divider}`},
                            borderBottom: {xs: `1px solid ${theme.palette.divider}`, md: 'none'},
                            bgcolor: 'background.default',
                            overflowY: {xs: 'visible', md: 'auto'}
                        })}>
                            {identityPanel}
                        </Box>

                        <Box component={'section'} aria-label={t('Node configuration')} tabIndex={0}
                             sx={{width: {xs: '100%', md: 'auto'}, flex: 1, p: 3, minWidth: 0, minHeight: 0,
                                 overflowY: {xs: 'visible', md: 'auto'}, overscrollBehavior: 'contain',
                                 // A column, so an editor with `fill` can take the full height.
                                 ...(filling && {display: 'flex', flexDirection: 'column'})}}>
                            {editor}
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{justifyContent: 'space-between', px: 3, py: 1.5}}>
                <Stack direction={'row'} gap={1}>
                    {!!editingNode && (
                        <Button color={'error'} onClick={handleDelete} disabled={readOnly || busy}>
                            {t('Delete node')}
                        </Button>
                    )}
                </Stack>

                <Stack direction={'row'} gap={1}>
                    <Button onClick={handleClose} disabled={saving}>
                        {t('Cancel')}
                    </Button>
                    <LoadingButton
                        variant={'contained'}
                        onClick={handleSave}
                        loading={saving}
                        disabled={readOnly}
                    >
                        {t('Save configuration')}
                    </LoadingButton>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default NodeConfigDialog;
